import { useEffect } from 'react';

interface SeoProps {
  title: string;
  description: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  structuredData?: Record<string, any> | Array<Record<string, any>>;
}

const SITE_NAME = 'Jobisa';
const DEFAULT_IMAGE = 'https://jobisa.in/wordmark.png';

function ensureMeta(selector: string, create: () => HTMLMetaElement) {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (!element) {
    element = create();
    document.head.appendChild(element);
  }
  return element;
}

function ensureCanonical() {
  let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  return link;
}

function ensureRobots() {
  return ensureMeta('meta[name="robots"]', () => {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    return meta;
  });
}

function getBaseUrl() {
  const host = window.location.hostname.toLowerCase();
  if (host.includes('recruiter.')) return 'https://recruiter.jobisa.in';
  return 'https://jobisa.in';
}

export default function Seo({ title, description, path = '/', image = DEFAULT_IMAGE, noindex = false, structuredData }: SeoProps) {
  useEffect(() => {
    const fullTitle = `${title} | ${SITE_NAME}`;
    const baseUrl = getBaseUrl();
    const canonicalUrl = new URL(path, `${baseUrl}/`).toString();

    document.title = fullTitle;

    const descriptionMeta = ensureMeta('meta[name="description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      return meta;
    });
    descriptionMeta.setAttribute('content', description);

    const ogTitle = ensureMeta('meta[property="og:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      return meta;
    });
    ogTitle.setAttribute('content', fullTitle);

    const ogDescription = ensureMeta('meta[property="og:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      return meta;
    });
    ogDescription.setAttribute('content', description);

    const ogType = ensureMeta('meta[property="og:type"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:type');
      return meta;
    });
    ogType.setAttribute('content', 'website');

    const ogUrl = ensureMeta('meta[property="og:url"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      return meta;
    });
    ogUrl.setAttribute('content', canonicalUrl);

    const ogSiteName = ensureMeta('meta[property="og:site_name"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:site_name');
      return meta;
    });
    ogSiteName.setAttribute('content', SITE_NAME);

    const ogImage = ensureMeta('meta[property="og:image"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:image');
      return meta;
    });
    ogImage.setAttribute('content', image);

    const twitterCard = ensureMeta('meta[name="twitter:card"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:card');
      return meta;
    });
    twitterCard.setAttribute('content', 'summary_large_image');

    const twitterTitle = ensureMeta('meta[name="twitter:title"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:title');
      return meta;
    });
    twitterTitle.setAttribute('content', fullTitle);

    const twitterDescription = ensureMeta('meta[name="twitter:description"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:description');
      return meta;
    });
    twitterDescription.setAttribute('content', description);

    const twitterImage = ensureMeta('meta[name="twitter:image"]', () => {
      const meta = document.createElement('meta');
      meta.setAttribute('name', 'twitter:image');
      return meta;
    });
    twitterImage.setAttribute('content', image);

    ensureCanonical().setAttribute('href', canonicalUrl);
    ensureRobots().setAttribute('content', noindex ? 'noindex,nofollow' : 'index,follow');

    const previousScripts = Array.from(document.head.querySelectorAll('script[data-jobisa-seo="true"]'));
    previousScripts.forEach((script) => script.remove());

    if (structuredData) {
      const list = Array.isArray(structuredData) ? structuredData : [structuredData];
      list.forEach((entry) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-jobisa-seo', 'true');
        script.text = JSON.stringify(entry);
        document.head.appendChild(script);
      });
    }
  }, [title, description, path, image, noindex, structuredData]);

  return null;
}
